import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import styles from './MediaSheet.module.scss';

import Icon from '../../widget/images/Icon';
import Window from '../../widget/windows/window';
import { Media } from '../../../dataTypes/mediaManagment/types';
import { Image } from '../../../dataTypes/mediaManagment/image_types';
import { Sound } from '../../../dataTypes/mediaManagment/sound_types';
import { Video } from '../../../dataTypes/mediaManagment/video_types';
import { AudioSlideshow } from '../../../dataTypes/mediaManagment/audioSlideshow_types';
import VideoSheet from './VideoSheet2';
import ImageSheet from './ImageSheet';
import SoundSheet from './SoundSheet';
import AudioSlideshowSheet from './AudioSlideshowSheet';
import MediaDropStep from './MediaDropStep';
import MediaGuideBanner, { MediaGuideState, guideStepOf } from './MediaGuideBanner';
import { MediaDropKind, MEDIA_KIND_TYPE_NAME, injectFileIntoSheet } from './mediaDropHelpers';

export interface MediaSheetProps {
  media?: Media;
  mediaType?: string;
  onCreate?: (id: number) => void;
  onEdit?: (id: number) => void;
  onClose?: () => void;
  uploadProgressFunction?: (progress: number) => void;
}

export const MediaSheet = ({
  media,
  mediaType,
  onCreate,
  onEdit,
  onClose,
  uploadProgressFunction,
}: MediaSheetProps) => {
  const isEditMode = media !== undefined;

  const mediaTypeMapping: { [key: string]: string } = {
    Video: 'Vidéo',
    Image: 'Image',
    Sound: 'Son',
    AudioSlideshow: 'Diapositive',
  };

  const mediaToCreateTypeNames = ['Vidéo', 'Diapositive', 'Image', 'Son'];
  const [selectedMediaToCreateType, setSelectedMediaToCreateType] = useState(
    isEditMode ? mediaTypeMapping[media.mediaType] : mediaType ? mediaTypeMapping[mediaType] : 'Vidéo',
  );
  const [informationMessage, setInformationMessage] = useState(<></>);
  const [showInformationWindow, setShowInformationWindow] = useState(false);
  const [id, setId] = useState(0);
  const creationWindowRef = useRef<HTMLDivElement>(null);

  // Création guidée : « Créer un média » demande d'abord le fichier. Le type (vidéo, son,
  // image) en découle, puis la fiche s'ouvre avec le média déjà dans son aperçu et un
  // accompagnement qui demande le nom, puis la catégorie. Seule la diapositive, faite d'une
  // image ET d'un son, n'a pas de fichier à déposer : elle ouvre directement sa fiche.
  const needsDropStep = !isEditMode && mediaType !== 'AudioSlideshow';
  const restrictKind: MediaDropKind | undefined =
    mediaType === 'Video' ? 'video' : mediaType === 'Sound' ? 'audio' : mediaType === 'Image' ? 'image' : undefined;
  const [dropDone, setDropDone] = useState(!needsDropStep);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingKind, setPendingKind] = useState<MediaDropKind | null>(null);
  const [guideState, setGuideState] = useState<MediaGuideState>({ title: '', categoryChosen: false });
  const guideStateRef = useRef(guideState);
  guideStateRef.current = guideState;
  const contentRef = useRef<HTMLDivElement>(null);
  // Vidéo guidée : l'écran tient dans la fenêtre et seule la fiche défile. Un `sticky` ne peut pas
  // garder le bandeau visible ici : le panneau d'onglets (overflow-y: auto) capte son défilement
  // sans jamais défiler lui-même.
  const [guidedHeight, setGuidedHeight] = useState<number | null>(null);

  // Emplacement offert aux fiches pour y projeter leurs boutons d'action, à
  // droite du sélecteur de type. Un état et non une ref : la fiche est un
  // enfant, elle doit être RE-RENDUE une fois l'élément monté, ce qu'une ref ne
  // déclenche pas. Il vaut null tant que le sélecteur n'est pas affiché (mode
  // édition, ou type imposé) — la fiche garde alors ses boutons en bas.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

  // Emplacement jumeau du précédent, à gauche de la ligne d'en-tête : en édition
  // la fiche y projette son bandeau Id / Uuid / Date de création, qui occupait
  // sinon une ligne entière pour trois valeurs courtes.
  const [metaSlot, setMetaSlot] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
	  
	console.log ('  selectedMediaToCreateType = ' , selectedMediaToCreateType)
    if (creationWindowRef.current) {
      creationWindowRef.current.scrollTop = 0;
    }
  }, [selectedMediaToCreateType]);

  const showDropStep = !isEditMode && !dropDone;
  const isVideoType = selectedMediaToCreateType === mediaToCreateTypeNames[0];
  const isSlideshowType = selectedMediaToCreateType === mediaToCreateTypeNames[1];
  const guideStep = guideStepOf(guideState, isVideoType);

  const handleFileDropped = (file: File, kind: MediaDropKind) => {
    setGuideState({ title: '', categoryChosen: false });
    setSelectedMediaToCreateType(MEDIA_KIND_TYPE_NAME[kind]);
    setPendingFile(file);
    setPendingKind(kind);
    setDropDone(true);
  };

  const handleSlideshowChosen = () => {
    setGuideState({ title: '', categoryChosen: false });
    setSelectedMediaToCreateType(mediaToCreateTypeNames[1]);
    setPendingFile(null);
    setPendingKind(null);
    setDropDone(true);
  };

  // Revenir au dépôt abandonne la saisie en cours : la fiche, démontée, repart vide.
  const handleChangeMedia = () => {
    setPendingFile(null);
    setPendingKind(null);
    setDropDone(false);
  };

  const handleDropCancel = () => {
    if (onClose) {
      onClose();
    } else if (onCreate) {
      onCreate(0);
    }
  };

  // Le fichier déposé est remis à la fiche une fois montée, par le même événement que le
  // sélecteur de son champ (voir injectFileIntoSheet) : aperçu, durée et validation suivent.
  // Le champ peut n'être monté qu'un instant plus tard, d'où les quelques essais.
  useEffect(() => {
    if (isEditMode || !dropDone || !pendingFile || !pendingKind) return;
    let cancelled = false;
    let tries = 0;
    let timer: number | undefined;
    const attempt = () => {
      if (cancelled) return;
      if (contentRef.current && injectFileIntoSheet(contentRef.current, pendingFile, pendingKind)) return;
      tries += 1;
      if (tries < 60) timer = window.setTimeout(attempt, 50);
      else console.warn("[media] Le fichier déposé n'a pas trouvé son champ dans la fiche");
    };
    attempt();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [isEditMode, dropDone, pendingFile, pendingKind]);

  useLayoutEffect(() => {
    if (isEditMode || !dropDone || !isVideoType) {
      setGuidedHeight(null);
      return;
    }
    const update = () => {
      const box = contentRef.current;
      if (box) setGuidedHeight(Math.max(480, window.innerHeight - (box.getBoundingClientRect().top + window.scrollY) - 12));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isEditMode, dropDone, isVideoType]);

  // Accompagnement : le curseur arrive dans le nom ; la catégorie n'est présentée qu'une fois
  // la zone de nom quittée — pas dès la première lettre, sans quoi la page défilerait sous la main.
  useEffect(() => {
    if (isEditMode || !dropDone) return;
    contentRef.current
      ?.querySelector<HTMLElement>('[data-guide="title"] input, [data-guide="title"] textarea')
      ?.focus();
  }, [isEditMode, dropDone]);

  useEffect(() => {
    if (isEditMode || !dropDone || !isVideoType) return;
    const box = contentRef.current;
    if (!box) return;
    const onFocusOut = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      const state = guideStateRef.current;
      if (target && target.closest('[data-guide="title"]') && state.title.trim() !== '' && !state.categoryChosen) {
        box.querySelector<HTMLElement>('[data-guide="category"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    box.addEventListener('focusout', onFocusOut);
    return () => box.removeEventListener('focusout', onFocusOut);
  }, [isEditMode, dropDone, isVideoType]);

  const handleCreationWindowCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleVideoCreation = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>La vidéo n°{id} a été créée</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleVideoEdition = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>La vidéo n°{id} a été modifiée</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleAudioSlideshowCreation = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>Le diaporama n°{id} a été créé</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleAudioSlideshowEdition = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>Le diaporama n°{id} a été modifié</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleSoundCreation = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>Le son n°{id} a été créé</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleSoundEdition = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>Le son n°{id} a été modifié</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleImageCreation = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>L'image n°{id} a été créée</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleImageEdition = (id: number) => {
    if (id !== 0) {
      setInformationMessage(<>L'image n°{id} a été modifiée</>);
      setShowInformationWindow(true);
      setId(id);
    } else {
      if (isEditMode) {
        if (onEdit) {
          onEdit(id);
        }
      } else {
        if (onCreate) {
          onCreate(id);
        }
      }
    }
  };

  const handleInformationWindowClosure = () => {
    setShowInformationWindow(false);
    if (isEditMode) {
      if (onEdit) {
        onEdit(id);
      }
    } else {
      if (onCreate) {
        onCreate(id);
      }
    }
  };

  return (
    <div className={classNames(onClose ? styles.creation_window_root : styles.root)}>
      {showInformationWindow && (
        <Window
          message={informationMessage}
          onClickOnValidateButton={handleInformationWindowClosure}
        />
      )}
      {/* Croix de fermeture, positionnée en haut à droite */}
      {onClose && (
        <div className={classNames(styles.creation_window_close_button)}>
          <Icon name="cross" onClick={handleCreationWindowCancel} color="var(--color-6)" hoverColor="var(--color-Y)" />
        </div>
      )}
      {/* Contenu avec ascenseur et espace sous la croix */}
      <div className={classNames(styles.creation_window_scrollable)} ref={creationWindowRef}>
        <div className={classNames(styles.creation_window_content)} ref={contentRef}
             style={guidedHeight !== null ? { height: guidedHeight, display: 'flex', flexDirection: 'column' } : undefined}>
          {showDropStep && (
            <MediaDropStep
              restrictTo={restrictKind}
              onFile={handleFileDropped}
              onSlideshow={handleSlideshowChosen}
              onCancel={handleDropCancel}
            />
          )}
          {!isEditMode && dropDone && (
            <MediaGuideBanner
              heading={isVideoType ? 'Nouvelle vidéo' : isSlideshowType ? 'Nouvelle diapositive' : selectedMediaToCreateType === mediaToCreateTypeNames[3] ? 'Nouveau son' : 'Nouvelle image'}
              state={guideState}
              needsCategory={isVideoType}
              doneMessage={isVideoType
                ? 'Tout est prêt'
                : isSlideshowType
                  ? "Ajoutez l'image et le son, puis cliquez sur « Créer »"
                  : 'Tout est prêt : cliquez sur « Créer »'}
              onChangeMedia={needsDropStep ? handleChangeMedia : undefined}
              actionsRef={isVideoType ? setActionsSlot : undefined}
            />
          )}
          {/* L'en-tête ne sert plus qu'à l'édition d'une vidéo : en création, les boutons
              rejoignent le bandeau d'étapes. */}
          {isEditMode && isVideoType && (
            <div className={classNames(styles.creation_window_header, styles.creation_window_header_edit)}>
              <div className={classNames(styles.creation_window_meta)} ref={setMetaSlot} />
              <div className={classNames(styles.creation_window_actions)} ref={setActionsSlot} />
            </div>
          )}
          {!showDropStep && selectedMediaToCreateType === mediaToCreateTypeNames[0] && !isEditMode && (
            <VideoSheet
              onCreate={handleVideoCreation}
              onGuideState={setGuideState}
              uploadProgressFunction={uploadProgressFunction}
              actionsSlot={actionsSlot}
            />
          )}
          {!showDropStep && selectedMediaToCreateType === mediaToCreateTypeNames[1] && !isEditMode && (
            <AudioSlideshowSheet onCreate={handleAudioSlideshowCreation} onGuideState={setGuideState} />
          )}
          {!showDropStep && selectedMediaToCreateType === mediaToCreateTypeNames[2] && !isEditMode && (
            <ImageSheet onCreate={handleImageCreation} onGuideState={setGuideState} />
          )}
          {!showDropStep && selectedMediaToCreateType === mediaToCreateTypeNames[3] && !isEditMode && (
            <SoundSheet onCreate={handleSoundCreation} onGuideState={setGuideState} />
          )}
          {selectedMediaToCreateType === mediaToCreateTypeNames[0] && isEditMode && (
            <VideoSheet
              onEdit={handleVideoEdition}
              video={media as Video}
              uploadProgressFunction={uploadProgressFunction}
              actionsSlot={actionsSlot}
              metaSlot={metaSlot}
            />
          )}
          {selectedMediaToCreateType === mediaToCreateTypeNames[1] && isEditMode && (
            <AudioSlideshowSheet onEdit={handleAudioSlideshowEdition} audioSlideshow={media as AudioSlideshow} />
          )}
          {selectedMediaToCreateType === mediaToCreateTypeNames[2] && isEditMode && (
            <ImageSheet onEdit={handleImageEdition} image={media as Image} />
          )}
          {selectedMediaToCreateType === mediaToCreateTypeNames[3] && isEditMode && (
            <SoundSheet onEdit={handleSoundEdition} sound={media as Sound} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaSheet;
